import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { COPY } from "../shared/copy";
import { PronounPicker } from "../components/PronounPicker";
import {
  promptsFor, OPEN_LANE, buildUpdateText, hasAnySubstance, checkInFraming,
  type CheckInPrompt, type CrewPurpose, type PromptAnswer,
} from "../shared/checkin-prompts";

type Phase = "loading" | "guided" | "freestyle" | "working" | "failed" | "all-known";
type SeasonState = "loading" | "ready" | "none" | "over";

// Check-in, guided the first time and open every time after.
//
// The single blank box this replaces works for natural sharers and fails
// everyone else. Now a first check-in climbs two short levels of prompts, every
// one of them skippable and the screen saying so, with an open lane at the end
// so a talker is never boxed in by scaffolding built for someone else.
//
// Once you have checked in, you get the open box instead of the whole ladder.
// You already know what this is and you have seen what other people wrote. The
// ladder is for the first time, and for anyone who freezes at a blank field.
export function CheckIn() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [seasonId, setSeasonId] = useState<string | null>(null);
  const [seasonState, setSeasonState] = useState<SeasonState>("loading");
  const [phase, setPhase] = useState<Phase>("loading");
  const [prompts, setPrompts] = useState<CheckInPrompt[]>([]);
  const [purpose, setPurpose] = useState<CrewPurpose>("keeping_up");
  // Asked once, on the first step, and only of somebody who has never answered.
  const [askPronouns, setAskPronouns] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [text, setText] = useState("");
  const [skippedAll, setSkippedAll] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      const { data: season } = await supabase
        .from("seasons").select("id, status, purpose").eq("league_id", leagueId).maybeSingle();
      if (!alive) return;

      if (!season?.id) { setSeasonState("none"); setPhase("guided"); return; }
      if (season.status === "complete") { setSeasonState("over"); setPhase("guided"); return; }
      setSeasonId(season.id);
      setSeasonState("ready");

      // The crew's purpose re-angles the prompts. A crew that has not set one is
      // keeping up, which is what most crews are.
      const p = ((season as any).purpose as CrewPurpose) ?? "keeping_up";
      setPurpose(p);
      setPrompts(promptsFor(p));

      // Have they checked in before, in this crew? Only the count is read, never
      // anyone else's content, and check_ins are owner-only anyway.
      const { data: me } = await supabase
        .from("users").select("pronouns, pronouns_asked").eq("id", user.id).maybeSingle();
      if (alive) setAskPronouns(!me?.pronouns && !me?.pronouns_asked);

      const { count } = await supabase
        .from("check_ins")
        .select("id", { count: "exact", head: true })
        .eq("league_id", leagueId)
        .eq("user_id", user.id);
      if (!alive) return;
      setPhase((count ?? 0) > 0 ? "freestyle" : "guided");
    })();
    return () => { alive = false; };
  }, [leagueId, user]);

  const ladder = [...prompts, OPEN_LANE];
  const current = ladder[step];
  const answeredSoFar = (): PromptAnswer[] =>
    ladder.map((q) => ({ prompt: q.text, response: answers[q.id] ?? "" }));

  function setAnswer(id: string, v: string) {
    setAnswers((a) => ({ ...a, [id]: v }));
  }

  async function send(update: string) {
    if (!seasonId || !update.trim()) return;
    setPhase("working");
    setErr("");
    try {
      const res = await api<any>(`/api/checkin/submit`, {
        body: { op: "create", seasonId, leagueId, text: update, inputType: "text" },
      });
      if (res.status === "ready") {
        navigate(`/league/${leagueId}/candidates`);
      } else if (res.status === "all_duplicates") {
        // Not a failure. Everything in this update restated something the crew
        // already has, which means they already said it, and telling them we
        // could not build questions would be both wrong and discouraging.
        setPhase("all-known");
      } else {
        setErr(describeFailure(res));
        setPhase("failed");
      }
    } catch (e: any) {
      setErr(
        e?.message?.includes("fetch")
          ? "Couldn't reach us. Check your connection and send it again."
          : "That didn't go through. Nothing you wrote was lost. Try again."
      );
      setPhase("failed");
    }
  }

  // Moving on, whether they answered or skipped. At the end of the ladder,
  // someone who skipped every single prompt goes to the open box rather than
  // being told they failed at a form.
  function advance() {
    if (step < ladder.length - 1) { setStep(step + 1); return; }
    const all = answeredSoFar();
    if (!hasAnySubstance(all)) { setSkippedAll(true); setPhase("freestyle"); return; }
    void send(buildUpdateText(all));
  }

  function describeFailure(res: any): string {
    if (res?.status === "provider_error") {
      return "Our question builder is having a problem on our end, not yours. Nothing you wrote was lost. Try again in a few minutes, and tell Brian if it keeps happening.";
    }
    return "We couldn't turn that into questions yet. Add a specific detail or two, like what changed, when, or who was there, then send it again.";
  }

  // The line that answers the actual fear. It sits on the screen where the
  // worry lives, not in a settings page nobody opens.
  const reassurance = (
    <p className="muted reassure">
      You approve every question before anyone sees it. Only what you pick gets used.
    </p>
  );

  if (phase === "loading") {
    return <div className="app"><div className="loading">Loading…</div></div>;
  }

  if (seasonState === "none") {
    return (
      <div className="app">
        <div className="header"><h1>Not yet</h1></div>
        <div className="card">
          <p>Your host hasn&apos;t set the season up yet. Once they do, you can send your update.</p>
        </div>
        <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>Crew home</button>
      </div>
    );
  }

  if (seasonState === "over") {
    return (
      <div className="app">
        <div className="header"><h1>Season&apos;s done</h1></div>
        <div className="card"><p>This season has wrapped, so updates aren&apos;t being used right now.</p></div>
        <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>See the final standings</button>
      </div>
    );
  }

  if (phase === "working") {
    return (
      <div className="app">
        <div className="spacer" />
        <div className="beat">
          <span className="spinner" />
          <div className="big">{COPY.checkIn.workingOnIt}</div>
          <p className="muted">Reading your update and writing questions about you. A few seconds.</p>
        </div>
        <div className="spacer" />
      </div>
    );
  }

  if (phase === "all-known") {
    return (
      <div className="app">
        <div className="header">
          <h1>Already got that</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Crew home</button>
        </div>
        <div className="card">
          <p>
            Everything in there is already covered by questions you&apos;ve got. Nothing was
            lost, and you don&apos;t need to do anything.
          </p>
          <p className="muted">
            If something new happened, tell us that part and we&apos;ll build from it.
          </p>
        </div>
        <button className="btn primary" onClick={() => { setText(""); setPhase("freestyle"); }}>
          Add something else
        </button>
        <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>
          Back to the crew
        </button>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="app">
        <div className="header">
          <h1>That didn&apos;t work</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Crew home</button>
        </div>
        <div className="card">
          <p className="error">{err || COPY.checkIn.genFailed}</p>
          <p className="muted">Your update is still here. Nothing was lost.</p>
        </div>
        <button className="btn primary" onClick={() => { setErr(""); setPhase(skippedAll ? "freestyle" : "guided"); }}>
          Back to my update
        </button>
      </div>
    );
  }

  // ---- The open box: returning members, and anyone who skipped the lot ----
  if (phase === "freestyle") {
    return (
      <div className="app">
        <div className="header">
          <h1>{skippedAll ? "No pressure" : checkInFraming(purpose).returning}</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Crew home</button>
        </div>
        <p className="muted">
          {skippedAll
            ? "Those didn't fit? That's fine. Write whatever you're comfortable with instead, however short."
            : "Tell the crew what's been going on. However much or little you want."}
        </p>
        <textarea
          className="input tall"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type whatever you want them to know…"
        />
        {reassurance}
        {err && <p className="error">{err}</p>}
        <button className="btn primary" disabled={!text.trim()} onClick={() => void send(text)}>
          Send it
        </button>
      </div>
    );
  }

  // ---- The guided ladder ----
  const value = answers[current.id] ?? "";
  const isLast = step === ladder.length - 1;

  return (
    <div className="app">
      <div className="header">
        <h1>{step === 0 ? checkInFraming(purpose).first : "Keep going"}</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Crew home</button>
      </div>

      {step === 0 && <p className="muted">{checkInFraming(purpose).lead}</p>}

      {step === 0 && askPronouns && user && (
        <PronounPicker
          userId={user.id}
          current={null}
          onDone={() => setAskPronouns(false)}
        />
      )}

      {/* Where they are, and that there is an end to it. */}
      <div className="progress" aria-label={`Question ${step + 1} of ${ladder.length}`}>
        {ladder.map((q, i) => (
          <span key={q.id} className={`progress-pip${i <= step ? " done" : ""}`} />
        ))}
      </div>

      <div className="card">
        <span className="tag">{step + 1} of {ladder.length}</span>
        <p className="prompt-text">{current.text}</p>
        {current.hint && <p className="muted">{current.hint}</p>}
        <textarea
          className="input tall"
          value={value}
          onChange={(e) => setAnswer(current.id, e.target.value)}
          placeholder="Type as much or as little as you want…"
        />
      </div>

      {reassurance}
      {err && <p className="error">{err}</p>}

      <button className="btn primary" disabled={!value.trim()} onClick={advance}>
        {isLast ? "Build my questions" : "Next"}
      </button>

      {/* Skipping is a real, plain option, not a hidden one. Saying so is what
          makes the whole thing safe to start. */}
      <button className="link center" onClick={advance}>
        {isLast ? "Skip this and build my questions" : "Skip this one"}
      </button>
      <p className="muted center" style={{ fontSize: 12 }}>
        You can skip any of these. Answer only what you want to.
      </p>
    </div>
  );
}
